import { ROUTE_IDS } from '../routes/ids.js';
import { canonicalRouteRegistry } from '../routes/routes.js';
import { CANONICAL_OPERATIONS_REGISTRY } from '../../operations/index.js';
import { resolvePersonaPermissions } from '../../access/canonical-personas.js';
import { getPersonaAccessProfile } from '../../access/personas/persona-access-profile.registry.js';
import { PERSONA_TO_SYSTEM_ROLE, getSystemRoleByPersona, } from '../../access/personas/canonical-system-roles.js';
/**
 * Generate a single persona test case from the authoritative PersonaAccessProfile.
 *
 * Route expectations are derived from the profile's structural route lists and
 * the permissions belonging to the persona's default permission bundles.
 */
export function generatePersonaCase(persona) {
    const profile = getPersonaAccessProfile(persona);
    const role = getSystemRoleByPersona(persona);
    const defaultPermissions = new Set(resolvePersonaPermissions(persona));
    const allowedSet = new Set(profile.navigation.primaryRouteIds.concat(profile.navigation.avatarRouteIds));
    const deniedSet = new Set(profile.navigation.structurallyDeniedRouteIds);
    const expectedRoutes = {};
    for (const route of canonicalRouteRegistry) {
        const routeId = route.routeId;
        if (route.authentication === 'public') {
            expectedRoutes[routeId] = { state: 'HIDDEN' };
            continue;
        }
        if (deniedSet.has(routeId)) {
            expectedRoutes[routeId] = { state: 'HIDDEN' };
            continue;
        }
        if (allowedSet.has(routeId)) {
            const operation = route.operationId ? CANONICAL_OPERATIONS_REGISTRY[route.operationId] : undefined;
            const permission = operation?.permission;
            const permitted = permission ? defaultPermissions.has(permission) : true;
            expectedRoutes[routeId] = permitted
                ? { state: 'AVAILABLE' }
                : { state: 'LOCKED_PERMISSION', reasonCode: 'PERMISSION_DENIED' };
            continue;
        }
        expectedRoutes[routeId] = { state: 'HIDDEN' };
    }
    const hiddenRoutes = [
        ROUTE_IDS.LOGIN,
        ROUTE_IDS.FORGOT_PASSWORD,
        ROUTE_IDS.RESET_PASSWORD,
        ROUTE_IDS.MFA_VERIFY,
        ROUTE_IDS.MFA_ENROLL,
    ];
    for (const routeId of hiddenRoutes) {
        if (!expectedRoutes[routeId]) {
            expectedRoutes[routeId] = { state: 'HIDDEN' };
        }
    }
    return {
        id: role.code,
        label: role.name,
        tier: persona,
        aal: profile.authentication.defaultAal,
        bundleIds: [...role.permissionBundleIds],
        enabledModules: [...profile.modules.baseline, ...profile.modules.optional],
        actorType: profile.actorTypes[0],
        expectedRoutes,
        description: `Generated expectations for ${role.name}`,
    };
}
export const GENERATED_PERSONA_CASES = Object.fromEntries(Object.keys(PERSONA_TO_SYSTEM_ROLE).map((persona) => [
    persona,
    generatePersonaCase(persona),
]));
